/**
 * Owner: Masa Bayu
 * File: AuthorRoutes.tsx
 * Date:Time: 2026-10-23 09:35
 * Description: /author/* route resolution — SEMUA laluan author dibalut
 *   `AuthorAccessGate` §467-C: kunci ialah sesi penerbit qxp-hub (gerbang membaca
 *   status `GET /v1/publisher/dashboard`, bukan tekaan klien). §412 tap Profile
 *   memaparkan Author Detail identiti HUB sebenar (slug daripada
 *   `identity.displayName`), bukan slug dataset demo. §468u: `STUDIO_ENTRY`
 *   (/author/studio) ialah SATU pintu penulisan (QxStudioScreen dalam app);
 *   `/qxeditor` alias ke sini; `/author/edition` dibuang.
 */

import type { ReactElement } from 'react';
import {
  AuthorPublicationsPanel,
  AuthorSecurityJourneyPanel,
  AuthorStorefrontPanel,
  DedicatedAnalyticsScreen,
  DedicatedEarningsScreen,
  DedicatedSalesScreen,
  ProfileShell,
  STUDIO_ENTRY,
} from '../author';
import { AuthorAccessGate } from '../author/private/AuthorAccessGate';
import { PrivateAuthorDashboard } from '../author/private/PrivateAuthorDashboard';
import { usePublisherDashboard } from '../author/private/publisher-dashboard';
import { slugifyAuthorName } from '../author/public/public-author-data';
import { AuthorProfileScreen } from '../author/public/AuthorProfileScreen';
import { QxStudioScreen } from './QxStudioScreen';

const RESERVED_AUTHOR_SECTIONS = new Set([
  'dashboard',
  'profile',
  'publications',
  'storefront',
  'sales',
  'earnings',
  'analytics',
  'studio',
  'settings',
  // §468u: laluan yang DIBUANG turut dikhaskan supaya ia tidak jatuh ke cabang
  // awam `/author/:id` dan tersalah papar halaman pengarang bernama 'edition'.
  'edition',
]);

/** Laluan studio PERIBADI sahaja yang dikunci; /author/:id awam tidak dikunci. */
const PRIVATE_AUTHOR_ROUTES = new Set([
  '/author',
  '/author/dashboard',
  '/author/profile',
  '/author/publications',
  '/author/storefront',
  '/author/sales',
  '/author/earnings',
  '/author/analytics',
  STUDIO_ENTRY,
  '/author/settings',
]);

/** Halaman awam bagi akaun penerbit yang sedang log masuk (slug = nama hub). */
function PrivateAuthorProfile({
  onNavigate,
}: {
  readonly onNavigate: (path: string) => void;
}): ReactElement {
  const dash = usePublisherDashboard();
  const slug = dash.view === null ? '' : slugifyAuthorName(dash.view.identity.displayName);
  return <AuthorProfileScreen authorId={slug} onNavigate={onNavigate} />;
}

export interface AuthorRoutesProps {
  readonly path: string;
  readonly onNavigate: (path: string) => void;
}

function AuthorRouteContent({ path, onNavigate }: AuthorRoutesProps): ReactElement {
  if (path === '/author' || path === '/author/dashboard') {
    return <PrivateAuthorDashboard onNavigate={onNavigate} />;
  }

  if (path === '/author/profile') {
    return <PrivateAuthorProfile onNavigate={onNavigate} />;
  }

  if (path === '/author/publications') {
    return (
      <ProfileShell activeId="studio" onNavigate={onNavigate}>
        <AuthorPublicationsPanel onNavigate={onNavigate} />
      </ProfileShell>
    );
  }

  if (path === '/author/storefront') {
    return (
      <ProfileShell activeId="studio" onNavigate={onNavigate}>
        <AuthorStorefrontPanel onNavigate={onNavigate} />
      </ProfileShell>
    );
  }

  if (path === '/author/sales') {
    return (
      <ProfileShell activeId="sales" onNavigate={onNavigate}>
        <DedicatedSalesScreen onNavigate={onNavigate} />
      </ProfileShell>
    );
  }

  if (path === '/author/earnings') {
    return (
      <ProfileShell activeId="sales" onNavigate={onNavigate}>
        <DedicatedEarningsScreen onNavigate={onNavigate} />
      </ProfileShell>
    );
  }

  if (path === '/author/analytics') {
    return (
      <ProfileShell activeId="activity" onNavigate={onNavigate}>
        <DedicatedAnalyticsScreen onNavigate={onNavigate} />
      </ProfileShell>
    );
  }

  if (path === STUDIO_ENTRY) {
    // §470→Fasa B-0 — Studio dimuat sebagai chunk malas dalam app ini, bukan keluar halaman.
    return <QxStudioScreen onNavigate={onNavigate} />;
  }

  if (path === '/author/settings') {
    return (
      <ProfileShell activeId="account" onNavigate={onNavigate}>
        <AuthorSecurityJourneyPanel onNavigate={onNavigate} />
      </ProfileShell>
    );
  }

  // §412 — /author/:id halaman Author Detail replika mockup (id = slug katalog).
  const authorMatch = /^\/author\/([^/]+)$/.exec(path);
  const authorId = authorMatch?.[1];
  if (authorId !== undefined && !RESERVED_AUTHOR_SECTIONS.has(authorId)) {
    return <AuthorProfileScreen authorId={authorId} onNavigate={onNavigate} />;
  }

  return (
    <section className="route-panel" aria-labelledby="author-route-title">
      <h2 id="author-route-title">Unknown shell path</h2>
      <p>
        <code>{path}</code>
      </p>
    </section>
  );
}

export function AuthorRoutes({ path, onNavigate }: AuthorRoutesProps): ReactElement {
  const content = <AuthorRouteContent path={path} onNavigate={onNavigate} />;
  if (!PRIVATE_AUTHOR_ROUTES.has(path)) {
    // Halaman awam (/author/:id) dan laluan tidak dikenali: tiada sesi penerbit diperlukan.
    return content;
  }
  return <AuthorAccessGate onNavigate={onNavigate}>{content}</AuthorAccessGate>;
}
